import { Component, Input, OnDestroy, OnInit, signal, WritableSignal } from '@angular/core';
import { AgGridAngular } from 'ag-grid-angular';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin, fromEvent, Observable } from 'rxjs';
import {  catchError, debounceTime } from 'rxjs/operators';
import { Store } from '@ngxs/store';
import { Document } from 'app/models/dto/document.model';
import {
  ColDef,
  ColumnRowGroupChangedEvent,
  FirstDataRenderedEvent,
  GridApi,
  GridState,
  GridOptions,
  GridReadyEvent,
  SelectionChangedEvent,
  ToolPanelVisibleChangedEvent,
  RowDataUpdatedEvent,
  RowClickedEvent,
  RowNode,
} from 'ag-grid-community';
import { LenderUser } from 'app/models/dto/lender-user.model';
import { MatButtonModule } from '@angular/material/button';
import {
  ColumnFilterModel
} from 'app/components/grid-components/grid-types';
import {
  documentsGridConfig,
  documentsGridDefaultColDef,
  documentsSizeColsToFit,
} from './documents-grid-config-helpers';
import {
  COL_DEF_FILE_NAME,
  COL_DEF_TYPE,
  COL_DEF_UPLOADED,
  COL_DEF_DYNAMIC_ACTIONS,
  COL_DEF_DYNAMIC_BORROWER_ACTIONS,
  COL_DEF_CONTACT,
} from './documents-grid-column-defs';
import { DocumentsHeaderComponent } from './documents-header/documents-header.component';
import { MatDialog } from '@angular/material/dialog';
import { documentsAllowedUploadFileTypes } from 'app/deal/advanced-deal/tabs/advanced-deal-documents/documents-allowed-upload-file-types';
import { 
  GetAdvancedDocumentRequirements,
  AdvancedDealDocumentPost,
  AttachAdvancedDealDocument,
  GetAdvancedDealDocuments
} from 'app/store/deal/deal-documents/deal-document.actions';
import { GetContactsForBorrower } from 'app/store/contact/contact.actions';
import JSZip from 'jszip';
import { FileWithCategory } from 'app/documents/document-upload-dialog/advanced-file-upload.component';
import { AdvancedFileUploadDialogComponent } from 'app/documents/document-upload-dialog/advanced-file-upload-dialog.component';
import { Borrower } from 'app/models/dto/borrower.model';
import { BorrowerState } from 'app/store/borrower/borrower/borrower.state';
import { LenderUserState } from 'app/store/lender-user/lender-user/lender-user.state';
import { DealDocumentState } from 'app/store/deal/deal-documents/deal-document.state';
import { DOCUMENT_CATEGORIES_REQUIRING_YEAR_STRING } from 'app/documents/document-upload-dialog/document-upload-constants';
import { DocumentCategory } from 'app/models/dto/document-category.model';
import { DealDocumentsService } from 'app/services/deals/deal-documents.service';

@Component({
  selector: 'app-documents-grid',
  imports: [
    AgGridAngular,
    MatButtonModule,
    DocumentsHeaderComponent
  ],
  templateUrl: './documents-grid.component.html',
  styleUrl: './documents-grid.component.scss'
})
export class DocumentsGridComponent implements OnInit, OnDestroy {
  @Input({required: true}) authUser: LenderUser | undefined;
  @Input({required: true}) documents: Document[];
  @Input() borrowerDocuments: Document[];
  @Input() dealId: number;
  @Input() borrowerId: number;
  @Input() loanProductCategory: string;
  @Input() documentCategories: DocumentCategory[];
  readonly documentsGridConfig = documentsGridConfig;
  readonly colDefDefault = documentsGridDefaultColDef;
  _isDownloading = false;
  _documentsGridApi: GridApi | undefined;
  hasEsignaturePlus = this._store.selectSignal<boolean>(LenderUserState.hasPermission('eaSignaturePlus'));
  business = this._store.selectSignal<Borrower>(BorrowerState.borrower);
  currentUser = this._store.selectSignal<LenderUser | undefined>(LenderUserState.lenderUser);

  documentsGridInitialState: GridState;
  selectedRows = signal<any[]>([]);
  isModifiedViewFilter: WritableSignal<boolean> = signal(false);
  currentFilterModel: WritableSignal<ColumnFilterModel> = signal<ColumnFilterModel>([]);
  colDefFileName: ColDef = COL_DEF_FILE_NAME;
  colDefType: ColDef = COL_DEF_TYPE;
  colDefUploadedOn: ColDef = COL_DEF_UPLOADED;
  colDefDynamicActions: ColDef = COL_DEF_DYNAMIC_ACTIONS;
  colDefDynamicBorrowerActions: ColDef = COL_DEF_DYNAMIC_BORROWER_ACTIONS;
  colDefContact: ColDef = COL_DEF_CONTACT;
  documentsGridOptions: GridOptions = Object.assign({}, {
    ...this.documentsGridConfig,
    defaultColDef: this.colDefDefault,
    // Set default sort order to "Uploaded on" column in descending order
    defaultSortModel: [
      {
        colId: 'created',
        sort: 'desc'
      }
    ]
  });
  loaded = signal(false);

  constructor(
    private _store: Store,
    private dialog: MatDialog,
    private dealDocumentsService: DealDocumentsService
  ) {
    // Subscribe to store state changes for loading overlay management
    this._store.select(DealDocumentState.dealLoadingDocuments)
      .pipe(takeUntilDestroyed())
      .subscribe(loadingFn => {
        if (this._documentsGridApi && this.dealId) {
          const isLoading = loadingFn(this.dealId);
          this._documentsGridApi.setGridOption('loading', isLoading);
          if (!isLoading && !this.loaded()) {
            this.loaded.set(true);
          }
        }
      });

    // Window resize handler - trigger auto-size strategy
    fromEvent(window, 'resize')
      .pipe(debounceTime(250), takeUntilDestroyed())
      .subscribe(() => {
        if (this._documentsGridApi) {
          // Trigger the auto-size strategy to recalculate column widths
          this._documentsGridApi.sizeColumnsToFit();
        }
      });
  }

  ngOnInit(): void {
    this.documentsGridOptions.columnDefs = [
      {
        ...this.colDefFileName,
        cellRendererParams: {
          dealId: this.dealId
        }
      },
      this.colDefContact,
      this.colDefType,
      this.colDefUploadedOn,
      {
        ...this.colDefDynamicActions,
        cellRendererParams: {
          dealId: this.dealId,
          borrowerId: this.borrowerId,
          loanProductCategory: this.loanProductCategory,
          business: this.business(),
          currentUserInstitutionId: this.currentUser()?.institution_id
        }
      },
      {
        ...this.colDefDynamicBorrowerActions,
        cellRendererParams: {
          dealId: this.dealId,
          borrowerId: this.borrowerId,
          loanProductCategory: this.loanProductCategory,
          business: this.business(),
          currentUserInstitutionId: this.currentUser()?.institution_id
        }
      }
    ];

    this._store.dispatch(new GetContactsForBorrower(this.borrowerId));
  }

  ngOnDestroy(): void {}

  onGridReady(params: GridReadyEvent) {
    this._documentsGridApi = params.api;
    // Set initial loading state - the subscription will handle updates
    const loadingFn = this._store.selectSnapshot(DealDocumentState.dealLoadingDocuments);
    const isLoading = loadingFn(this.dealId);
    this._documentsGridApi.setGridOption('loading', isLoading);
    if (!isLoading && !this.loaded()) {
      this.loaded.set(true);
    }
    this.calculateDefaultColumns();
  }

  onFirstDataRendered(event: FirstDataRenderedEvent): void {
    setTimeout(() => documentsSizeColsToFit(event.api), 500);
  }

  onColumnRowGroupChanged(event: ColumnRowGroupChangedEvent): void {
    documentsSizeColsToFit(event.api);
  }

  calculateDefaultColumns() {
    const columnsState: { colId: string, hide: boolean | undefined }[] = [];

    if (!this.dealId) {
      columnsState.push({
          colId: 'status',
          hide: true
      });

      columnsState.push({
        colId: 'contacts',
        hide: true
      });
    } else {
      columnsState.push({
        colId: 'actions',
        hide: true
      });

      if (!this.hasEsignaturePlus()) {
        columnsState.push({
          colId: 'contacts',
          hide: true
        });
      }
    }

      this._documentsGridApi?.applyColumnState({ state: columnsState });
  }

  onToolPanelVisibilityChanged(event: ToolPanelVisibleChangedEvent): void {
    setTimeout(() => documentsSizeColsToFit(event.api), 150);
  }

  onSelectionChanged(event: SelectionChangedEvent): void {
    this.selectedRows.set(event.api.getSelectedNodes());
  }

  onRowDataUpdated(event: RowDataUpdatedEvent): void {
    event.api.refreshCells({ force: true });
  }

  clearAllSelected(): void {
    this._documentsGridApi?.deselectAll();
  }

  handleUpload(event): void {
    this.openUploadDialog();
  }

  handleDocumentAttachment(document: Document): void {
      this._store.dispatch(new AttachAdvancedDealDocument(this.dealId, document));
  }

  handleRefreshDocuments(): void {
    this._store.dispatch(new GetAdvancedDealDocuments(this.dealId));
    this._store.dispatch(new GetAdvancedDocumentRequirements(this.dealId));
  }

  openUploadDialog(): void {
    const dialogRef = this.dialog.open(AdvancedFileUploadDialogComponent, {
      width: '400px',
      disableClose: true,
      restoreFocus: false,
      // Props for file upload. All are optional and have defaults if not given.
      data: {
        // Specific to the dialog.
        title: 'Upload',
        closeLabel: 'Close',
        categories: this.documentCategories,
        // Pass through to the file upload component.
        allowedFileTypes: documentsAllowedUploadFileTypes,
        // 20 MB - 1024 * 1024 * 20
        fileSizeLimit: 20971520,
        fileCount: 20,
        fileSelectText: 'Drop files to upload',
        buttonText: 'or choose files'
      },
    });


    // Save documents and show snackbar confirming save success.
    dialogRef.componentInstance.fileListInput.subscribe({
      next: (fileListWithCategories: FileWithCategory[]) => {
        const numberOfFiles = fileListWithCategories.length;
        let numberOfFilesUploaded = 0;
        fileListWithCategories.forEach((fileListItem) => {
          const formData = new FormData();
          if (this?.borrowerId) {
            formData.append('borrowerId', this.borrowerId.toString());
            formData.append('category', fileListItem.category);
            if (fileListItem.category === 'bankStatement') {
              formData.append('monthsString', fileListItem.monthsString!);
            }
            if (DOCUMENT_CATEGORIES_REQUIRING_YEAR_STRING.includes(fileListItem.category)) {
              formData.append('yearsString', fileListItem.yearsString!);
            }
          }

          if (fileListItem.contact && fileListItem.contact.id) {
            formData.append('contactId', fileListItem.contact.id.toString());
          }

          // Attach file to deal (in dealDocuments table) if this is a deal document upload.
          if (this?.dealId) {
            formData.append('attach', 'true');
            formData.append('dealId', this.dealId.toString());
            formData.append('category', fileListItem.category);
            if (fileListItem.category === 'bankStatement') {
              formData.append('monthsString', fileListItem.monthsString!);
            }
          }
          this._store.dispatch(new AdvancedDealDocumentPost(fileListItem.file, formData)).subscribe({
            next: () => {
              numberOfFilesUploaded++;
              if (numberOfFilesUploaded >= numberOfFiles) {
                dialogRef.close();

                if (this.dealId) {
                  this._store.dispatch(new GetAdvancedDocumentRequirements(this.dealId));
                }
              }
            }
          });
        });
      }
    });
  }

  handleDownload($event: Event): void {
    $event.stopPropagation();
    this.downloadSelected();
  }

  downloadSelected(): void {
    this._isDownloading = true;

    const zip = new JSZip();
    const downloadObservables: Observable<Blob>[] = [];
    this.selectedRows().forEach((rowNode: RowNode) => {
      downloadObservables.push(this.dealDocumentsService.downloadDocument(rowNode.data.id));
    });

    forkJoin(downloadObservables).pipe(
      catchError((e) => {
        // Prevent eternal spinner.
        this._isDownloading = false;
        throw e;
      }),
    ).subscribe(responses => {
      responses.forEach((response, index) => {
        zip.file(`${this.selectedRows()[index]?.data?.filename ?? `document-${index}.pdf`}`, response, { binary: true });
      });

      zip.generateAsync({ type: 'blob' }).then(content => {
        const fileURL = URL.createObjectURL(content);
        const link = document.createElement('a');
        link.href = fileURL;
        link.download = `${this.business().name} Deal(${this.dealId}) Documents.zip`;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        URL.revokeObjectURL(fileURL);
        document.body.removeChild(link);
      }).catch(error => {
        console.error('Error generating ZIP file:', error);
        this._isDownloading = false;
      });
      this.clearAllSelected();

      // Wait until next tick to prevent spinner from switching back to "Download"
      // before animation completes.
      setTimeout(() => {
        this._isDownloading = false;
      });
    });
  }

  onRowClicked(event: RowClickedEvent): void {
  }
}
